// reset-password.component.ts
import { Component, signal } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { EComponentSize, ELabelType, EIcon, IComponentSection } from '../../types';
import { DTO, Types } from 'shared-lib';
import { LabelComponent } from "../label/label.component";
import { CommonModule } from '@angular/common';
import { StepProgressComponent } from '../step-progress/step-progress.component';
import { DynamicFormComponent } from '../dynamic-form/dynamic-form.component';
import { ToastService } from '../toast/toast.service';
import { LoadingService } from '../loading/loading.service';
import { catchError, map, of } from 'rxjs';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';

@Component({
    standalone: true,
    imports: [LabelComponent, CommonModule, StepProgressComponent, DynamicFormComponent],
    selector: 'ui-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],

})
export class ResetPasswordComponent {
    isAuthenticated: boolean = false;
    steps: { name: string }[] = [{ name: "E-mail" }, { name: "Código" }, { name: "Alterar" }];
    step: number = 0;
    fields: IComponentSection[] = [];
    user?: DTO.UserDTO;
    loading = signal(true);

    //data
    email: string = '';
    code: string = '';
    correctCode: string = '';
    password: string = '';
    confirmPassword: string = '';

    EAlert = Types.EAlert;
    EComponentSize = EComponentSize;
    EIcon = EIcon;
    ELabelType = ELabelType;

    constructor(
        private authService: AuthService,
        private toastService: ToastService,
        private loadingService: LoadingService,
        private router: Router,
        private http: HttpClient) {

        this.getUserData();

    }

    async getUserData() {
        let userResponse = await this.authService.getUserInfo();

        if (userResponse){
            this.user = userResponse;
            this.isAuthenticated = true;
        }else{
            this.isAuthenticated = false;
        }

        this.getForm();
    }

    nextStep(index: number): void {
        if (index == 0 && this.email) {
            return;
        } else if ((index === 1 && !this.email) || (index === 1 && this.correctCode)) {
            return;
        } else if (index === 2 && !this.correctCode) {
            return;
        }

        this.step = index;
        this.getForm()
    }

    getForm() {
        this.fields = []
        if (!this.isAuthenticated) {
            switch (this.step) {
                case 0:
                    this.fields = [{
                        name: "E-mail",
                        selector: 'data',
                        content: [
                            {
                                component: 'TextComponent',
                                selector: 'email',
                                inputs: {
                                    placeHolder: 'Insira seu e-mail:',
                                    default: '',
                                    value: this.email,
                                    type: Types.EInput.EMAIL,
                                    required: true,
                                },
                            },
                        ],
                    }]
                    return;
                    break;

                case 1:
                    this.fields = [{
                        name: "Código",
                        selector: 'data',
                        content: [
                            {
                                component: 'TextComponent',
                                selector: 'code',
                                inputs: {
                                    placeHolder: 'Insira o código que você recebeu por e-mail:',
                                    default: '',
                                    value: this.code,
                                    type: Types.EInput.TEXT,
                                    required: true,
                                },
                            },
                        ],
                    }]
                    return;
                    break;
                default:
                    break;
            }
        }
        this.fields = [{
            name: "Senha",
            selector: 'data',
            content: [
                {
                    component: 'TextComponent',
                    selector: 'password',
                    inputs: {
                        placeHolder: 'Insira a nova senha',
                        default: '',
                        value: this.password,
                        type: Types.EInput.PASSWORD,
                        required: true,
                    },
                },
                {
                    component: 'TextComponent',
                    selector: 'confirmPassword',
                    inputs: {
                        placeHolder: 'Confirme a nova senha',
                        default: '',
                        value: this.confirmPassword,
                        type: Types.EInput.PASSWORD,
                        required: true,
                    },
                },
            ],
        },
        ]
    }

    submit(event: any): void {
        const data = event['data'];

        // caso nao esteja logado, usara os steps antes de tudo
        if (!this.isAuthenticated && this.step != 2) {
            let nextIndex = this.step + 1;

            if (this.step == 0) {
                this.email = data.email;
                //envia o codigo
                this.sendCode();
            }

            if (this.step == 1) {
                this.code = data.code;
                this.confirmCode(nextIndex)
                return;
            }

            this.nextStep(nextIndex);

            return;
        }

        this.password = data.password.trim();
        this.confirmPassword = data.confirmPassword.trim();

        if (!this.password || !this.confirmPassword || this.password != this.confirmPassword) {
            this.toastService.add(
                `As senhas não são iguais.`,
                30000,
                'warning'
            );
            return;
        }

        if (this.isAuthenticated) {
            this.changePasswordAuthenticated();
        } else {
            this.changePassword();
        }


    }

    sendCode() {
        this.loadingService.start();
        this.http
            .post<{ reset_code: string }>('userAccount/forgot-password', this.email)
            .pipe(
                map((response) => {
                    localStorage.setItem('reset_code', response.reset_code);

                    this.toastService.add(
                        `O código foi enviado para o seu e-mail!`,
                        30000,
                        'success'
                    );
                    this.getForm()
                    return true;
                }),
                catchError((error) => {
                    this.toastService.add(
                        error.error.message ?? 'Ocorreu um erro ao enviar seu código, tente novamente mais tarde.',
                        30000,
                        'danger'
                    );
                    return of(false);
                })
            )
            .subscribe();

        this.loadingService.stop();
        this.loading.set(false);
    }

    confirmCode(nextIndex: number) {
        if (this.correctCode) {
            this.code = this.correctCode;
            this.nextStep(nextIndex);
        } else {
            this.loadingService.start();
            const resetCode = localStorage.getItem("reset_code");
            if (!resetCode) {
                this.toastService.add(
                    `Nenhum código para confirmação encontrado.`,
                    30000,
                    'warning'
                );
                return;
            }
            this.http
                .post('userAccount/verify-reset-code', { code: this.code, storedHash: resetCode })
                .pipe(
                    map((response) => {
                        if (response == true) {
                            this.toastService.add(
                                `O código foi verificado!`,
                                30000,
                                'success'
                            );

                            this.correctCode = this.code;
                            this.nextStep(nextIndex);
                            return true;
                        } else {
                            this.toastService.add(
                                "Código incorreto, verifique sua caixa de entrada novamente.",
                                30000,
                                'danger'
                            );
                            return of(false);
                        }

                    }),
                    catchError((error) => {
                        this.toastService.add(
                            error.error.message ?? 'Ocorreu um erro ao verificar seu código, tente novamente mais tarde.',
                            30000,
                            'danger'
                        );
                        return of(false);
                    })
                )
                .subscribe();

            this.loadingService.stop();
            this.loading.set(false);

        }
    }

    changePassword() {
        const resetCode = localStorage.getItem("reset_code");

        const data = {
            email: this.email,
            code: this.correctCode,
            storedHash: resetCode,
            password: this.password
        }
        this.loadingService.start();
        this.http
            .post<{ reset_code: string }>('userAccount/change-password', data)
            .pipe(
                map((response) => {
                    localStorage.removeItem("reset_code");

                    this.toastService.add(
                        `Sua senha foi atualizada com sucesso, faça login!`,
                        30000,
                        'success'
                    );

                    setTimeout(() => {
                        this.router.navigate(['/login']);
                    }, 3000);

                    return true;
                }),
                catchError((error) => {
                    this.toastService.add(
                        error.error.message ?? 'Ocorreu um erro ao atualizar sua senha, tente novamente mais tarde.',
                        30000,
                        'danger'
                    );
                    return of(false);
                })
            )
            .subscribe();

        this.loadingService.stop();
        this.loading.set(false);
    }

    changePasswordAuthenticated() {
        this.loadingService.start();
        this.http
            .post<{ reset_code: string }>('userAccount/change-password-auth', this.password)
            .pipe(
                map((response) => {
                    localStorage.removeItem("reset_code");

                    this.toastService.add(
                        `Sua senha foi atualizada com sucesso!`,
                        30000,
                        'success'
                    );

                    return true;
                }),
                catchError((error) => {
                    this.toastService.add(
                        error.error.message ?? 'Ocorreu um erro ao atualizar sua senha, tente novamente mais tarde.',
                        30000,
                        'danger'
                    );
                    return of(false);
                })
            )
            .subscribe();

        this.loadingService.stop();
        this.loading.set(false);
    }
}
